import { useState, useEffect, TouchEvent } from "react";
import VideoPlayer from "./VideoPlayer";
import Description from "./Description";

import { videoStates, descriptionStates } from "../utils/constants";

import { HotspotControllerInterface } from "../types/HotspotControllerInterface";

import Nav from "./Nav";

// constants
const VIDEO_IN = "VIDEO_IN";
const VIDEO_LOOP = "VIDEO_LOOP";
const VIDEO_OUT = "VIDEO_OUT";

const VIDEO_BASE_PATH = "./assets/videos";

const VIDEO_PATH_IN = `${VIDEO_BASE_PATH}/{key}-in.mp4`;
const VIDEO_PATH_LOOP = `${VIDEO_BASE_PATH}/{key}-loop.mp4`;
const VIDEO_PATH_OUT = `${VIDEO_BASE_PATH}/{key}-out.mp4`;
const INIT_VIDEO_PATH = `${VIDEO_BASE_PATH}/{key}.mp4`;

const VIDEO_INIT = "intro-explosion";

const HotspotController = ({
  hotspotData,
  inView,
  isFF,
  onStartClick,
}: HotspotControllerInterface) => {
  // state for selected video index
  const [requestedIndex, setRequestedIndex] = useState<number>(0);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [prevIndex, setPrevIndex] = useState<number>(-1);

  const [initialLoad, setInitialLoad] = useState<boolean>(true);
  const [isIntro, setIsIntro] = useState<boolean>(true);

  const initVideo = hotspotData[selectedIndex];

  // state for video source
  const [inSrc, setInSrc] = useState<string | undefined>(initVideo.key);
  const [loopSrc, setLoopSrc] = useState<string | undefined>(initVideo.key);
  const [outSrc, setOutSrc] = useState<string>("");

  const [videoStateIn, setStateIn] = useState<string>(videoStates.LOADING);
  const [videoStateOut, setStateOut] = useState<string>(videoStates.LOADING);
  const [videoStateLoop, setStateLoop] = useState<string>(videoStates.LOADING);

  const [isAnimating, setIsAnimating] = useState<boolean>(false);

  const [xDown, setXDown] = useState<number | null>();
  const [yDown, setYDown] = useState<number | null>();

  // state for when to show/hide description panel
  const [descriptionState, setDescriptionState] = useState(
    descriptionStates.HIDE
  );

  // state for which video to show
  const [activeVideo, setActiveVideo] = useState("");

  const updateInVideo = (key: string) => {
    setInSrc(VIDEO_PATH_IN.replace("{key}", key));
  };

  const updateOutVideo = (key: string) => {
    isIntro
      ? setOutSrc(INIT_VIDEO_PATH.replace("{key}", key))
      : setOutSrc(VIDEO_PATH_OUT.replace("{key}", key));
  };

  const updateLoopVideo = (key: string) => {
    setLoopSrc(VIDEO_PATH_LOOP.replace("{key}", key));
  };

  // only on initializations
  useEffect(() => {
    const initVideo = hotspotData[selectedIndex];

    isFF ? setIsAnimating(false) : setIsAnimating(true);

    updateInVideo(initVideo.key);
    updateLoopVideo(initVideo.key);
    updateOutVideo(VIDEO_INIT);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (initialLoad && inView) {
      setInitialLoad(false);
      setActiveVideo(VIDEO_OUT);
    }
  }, [inView]);

  useEffect(() => {
    // EXCEPTION FOR INITIAL INTRO ANIMATION
    if (
      requestedIndex === selectedIndex &&
      isIntro &&
      videoStateOut === videoStates.ENDED
    ) {
      setTimeout(() => {
        setIsIntro(false);
        setIsAnimating(false);
        setTimeout(() => {
          setActiveVideo(VIDEO_IN); 
        }, 1000);
      }, 1000);
      
      return;
    }

    // NEW index requested
    if (requestedIndex !== selectedIndex) {
      const requestedVideo = hotspotData[requestedIndex];

      setDescriptionState(descriptionStates.HIDE);

      //QUICK SWITCH
      if (videoStateIn === videoStates.PLAYING) {
        setDescriptionState(descriptionStates.HIDE);
        updateOutVideo(requestedVideo.key);
        updateLoopVideo(requestedVideo.key);
        setActiveVideo("");
        updateInVideo(requestedVideo.key);
        setTimeout(() => {
          setActiveVideo(VIDEO_IN);
        }, 200);
        setSelectedIndex(requestedIndex);
        setIsAnimating(false);
        return;
      }

      updateInVideo(requestedVideo.key);

      // show OUT video
      if (videoStateIn === videoStates.READY) {
        setActiveVideo(VIDEO_OUT);
        updateLoopVideo(requestedVideo.key);
      }

      // select + switch to new IN
      if (videoStateOut === videoStates.ENDED) {
        setActiveVideo(VIDEO_IN);
        setSelectedIndex(requestedIndex);
        setIsAnimating(false);
      }
    } else {
      // empty IN
      if (videoStateLoop === videoStates.PLAYING) {
        setInSrc(undefined);
        updateOutVideo(hotspotData[selectedIndex].key);
        setTimeout(() => {
          setDescriptionState(descriptionStates.SHOW);
        }, 300);
      }

      // empty LOOP
      if (videoStateOut === videoStates.PLAYING && !isIntro) {
        setLoopSrc(undefined);
      }

      // show description
      if (videoStateIn === videoStates.PLAYING) {
        setTimeout(() => {
          setDescriptionState(descriptionStates.SHOW);
        }, 300);
      }

      // switch to LOOP
      if (videoStateIn === videoStates.ENDED) {
        setActiveVideo(VIDEO_LOOP);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedIndex, videoStateIn, videoStateLoop, videoStateOut]);

  useEffect(() => {
    if (activeVideo === VIDEO_IN && videoStateIn === videoStates.PLAYING) {
      updateOutVideo(hotspotData[selectedIndex].key);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeVideo, videoStateIn]);

  const handleHotspotClicked = (index: number) => {
    if (window.innerWidth < 1024 && videoStateOut === videoStates.PLAYING) {
      return;
    }

    setRequestedIndex(index);

    if (!inView && isFF && onStartClick) onStartClick();
  };

  const getTouches = (event: TouchEvent) => {
    return event.touches;
  };

  const handleTouchStart = (event: TouchEvent) => {
    const firstTouch = getTouches(event)[0];

    setXDown(firstTouch.clientX);
    setYDown(firstTouch.clientY);
  };

  const handleTouchMove = (event: TouchEvent) => {
    if (!xDown || !yDown) {
      return;
    }

    var xUp = event.touches[0].clientX;
    var yUp = event.touches[0].clientY;

    var xDiff = xDown - xUp;
    var yDiff = yDown - yUp;

    if (Math.abs(xDiff) > Math.abs(yDiff)) {
      if (xDiff > 0) {
        /* right swipe */
        if (selectedIndex + 1 > hotspotData.length - 1 || isAnimating) return;
        setIsAnimating(true);
        handleHotspotClicked(selectedIndex + 1);
      } else {
        /* left swipe */
        if (selectedIndex - 1 < 0 || isAnimating) return;
        setIsAnimating(true);
        handleHotspotClicked(selectedIndex - 1);
      }
    }

    setXDown(null);
    setYDown(null);
  };

  return (
    <>
      <div className={`media ${isIntro ? 'is-intro' : ''}`}>
        <VideoPlayer
          type="IN"
          src={inSrc}
          active={activeVideo === VIDEO_IN}
          loop={false}
          updateState={setStateIn}
        />
        <VideoPlayer
          type="LOOP"
          src={loopSrc}
          active={activeVideo === VIDEO_LOOP}
          loop={true}
          updateState={setStateLoop}
        />
        <VideoPlayer
          type="OUT"
          src={outSrc}
          active={activeVideo === VIDEO_OUT}
          shouldShow={isIntro}
          loop={false}
          updateState={setStateOut}
        />
      </div>
      <Description
        data={hotspotData}
        state={descriptionState}
        selectedIndex={selectedIndex}
        handleTouchStart={handleTouchStart}
        handleTouchMove={handleTouchMove}
      />
      {hotspotData && (
        <Nav
          hotspotData={hotspotData}
          requestedIndex={requestedIndex}
          selectedIndex={selectedIndex}
          prevIndex={prevIndex}
          handleHotspotClicked={handleHotspotClicked}
          setPrevIndex={setPrevIndex}
          isAnimating={isAnimating}
          setIsAnimating={setIsAnimating}
          isIntro={isIntro}
        />
      )}
      {!inView && !isFF && (
        <button
          className="btn-start stat-button-link"
          name="diesel engine features"
          onClick={onStartClick}
        >
          <div className="inner">Click to Play</div>
        </button>
      )}
    </>
  );
};

export default HotspotController;
